/*
 * <faq-tabs> — sticky left-hand category nav (scrollspy) for a run of Dawn
 * `collapsible-content` sections (sections/custom-faq-tabs.liquid).
 *
 * IMPORTANT — why a "shell": Shopify's theme editor forbids one section's DOM
 * (`.shopify-section`) being nested inside another and reports it as "Broken
 * HTML". So instead of moving the group sections INTO this controller section,
 * the component builds a plain <div class="faq-tabs__shell"> as a SIBLING of the
 * controller (a direct child of <main>) and moves the nav + the group sections
 * into THAT. The groups then live inside a <div>, never inside another section.
 * The original controller markup stays hidden (it never gets .is-ready).
 *
 * Interaction: all groups stay visible, stacked on the right; clicking a category
 * smooth-scrolls to its group, and the active nav item is highlighted from scroll
 * position via an IntersectionObserver (scrollspy).
 *
 * DESKTOP-ONLY: the whole quick-category enhancement is gated on the
 * `(min-width: 990px)` breakpoint. On mobile (≤989px) the component builds
 * nothing — no shell, no nav, no scrollspy — and the group sections stay in their
 * original stacked, all-visible flow exactly like the no-JS fallback. A matchMedia
 * listener switches reactively when the viewport crosses the breakpoint: build on
 * entering desktop, tear down (restore the stacked groups) on entering mobile.
 *
 * No-JS: the controller stays hidden and the groups remain stacked normally.
 */
if (!customElements.get('faq-tabs')) {
  customElements.define(
    'faq-tabs',
    class FaqTabs extends HTMLElement {
      connectedCallback() {
        this.host = this.closest('.shopify-section') || this;
        this.controller = this.closest('.faq-tabs') || this; // original (hidden) wrapper
        if (!this.host.id) return;

        this.activeIndex = 0;
        this.sections = [];
        this.tabs = [];
        this.intersecting = new Set();
        this.lockUntil = 0;
        this.reduceMotion =
          window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.onSectionLoad = this.onSectionLoad.bind(this);
        this.onSectionUnload = this.onSectionUnload.bind(this);
        this.onSectionSelect = this.onSectionSelect.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.onResize = this.onResize.bind(this);
        this.onBreakpointChange = this.onBreakpointChange.bind(this);

        // Desktop-only enhancement: only build the nav at ≥990px. On mobile the
        // groups stay in their original stacked flow (no-JS fallback look).
        this.desktopMq = window.matchMedia('(min-width: 990px)');

        if (this.desktopMq.matches) this.build();

        // React when the viewport crosses the breakpoint: build on entering
        // desktop, tear down (restore the stacked groups) on entering mobile.
        if (this.desktopMq.addEventListener) {
          this.desktopMq.addEventListener('change', this.onBreakpointChange);
        } else if (this.desktopMq.addListener) {
          this.desktopMq.addListener(this.onBreakpointChange); // Safari < 14
        }

        document.addEventListener('shopify:section:load', this.onSectionLoad);
        document.addEventListener('shopify:section:unload', this.onSectionUnload);
        document.addEventListener('shopify:section:select', this.onSectionSelect);

        // The nav is centred with NATIVE position:sticky (no per-frame JS while
        // scrolling → zero lag). JS only runs on scroll to keep --faq-nav-offset
        // fresh (cheap, change-guarded) and on resize to recompute the sticky
        // metrics. Attached unconditionally so it works even without a header.
        // Both handlers no-op on mobile (nothing is built there).
        this.header = document.querySelector('.section-header');
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onResize, { passive: true });
        this.onResize();
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.desktopMq) {
          if (this.desktopMq.removeEventListener) {
            this.desktopMq.removeEventListener('change', this.onBreakpointChange);
          } else if (this.desktopMq.removeListener) {
            this.desktopMq.removeListener(this.onBreakpointChange);
          }
        }
        document.removeEventListener('shopify:section:load', this.onSectionLoad);
        document.removeEventListener('shopify:section:unload', this.onSectionUnload);
        document.removeEventListener('shopify:section:select', this.onSectionSelect);
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onResize);
        if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
        if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
      }

      /* Build on entering desktop; restore the plain stacked groups on mobile. */
      onBreakpointChange(event) {
        if (event.matches) this.build();
        else this.teardown();
      }

      /* Build (or reuse) the sibling shell that holds the nav + the group panels. */
      ensureShell() {
        var shellId = this.host.id + '-shell';
        var existing = document.getElementById(shellId);
        if (existing) {
          this.shell = existing;
        } else {
          var headingEl = this.querySelector('.faq-tabs__heading');
          var headingHtml = headingEl ? '<div class="faq-tabs__heading">' + headingEl.innerHTML + '</div>' : '';

          this.shell = document.createElement('div');
          this.shell.id = shellId;
          this.shell.className = (this.controller.className || 'faq-tabs') + ' faq-tabs__shell';
          this.shell.innerHTML =
            '<div class="faq-tabs__inner page-width">' +
            headingHtml +
            '<div class="faq-tabs__layout">' +
            '<nav class="faq-tabs__nav" aria-label="FAQ categories"><div class="faq-tabs__tablist"></div></nav>' +
            '<div class="faq-tabs__panels"></div>' +
            '</div></div>';

          this.host.parentNode.insertBefore(this.shell, this.host.nextSibling);
        }

        this.nav = this.shell.querySelector('.faq-tabs__nav');
        this.tablist = this.shell.querySelector('.faq-tabs__tablist');
        this.panels = this.shell.querySelector('.faq-tabs__panels');
        this.root = this.shell;
      }

      isCategorySection(el) {
        return (
          el &&
          el.classList &&
          el.classList.contains('shopify-section') &&
          el.querySelector('.collapsible-content')
        );
      }

      /* Move the consecutive category sections that follow the shell into its panels. */
      absorbFollowing() {
        var sib = this.shell.nextElementSibling;
        while (sib) {
          var next = sib.nextElementSibling;
          if (sib.classList && sib.classList.contains('shopify-section')) {
            if (this.isCategorySection(sib)) {
              sib.classList.add('faq-tabs__panel');
              this.panels.appendChild(sib);
            } else {
              break;
            }
          }
          sib = next;
        }
      }

      build() {
        // Desktop-only: never construct the shell / nav on mobile. Leaves the
        // groups in their original stacked flow (no-JS fallback).
        if (!this.desktopMq.matches) {
          this.teardown();
          return;
        }

        this.ensureShell();
        this.absorbFollowing();

        this.sections = Array.from(this.panels.children).filter(function (el) {
          return el.classList.contains('faq-tabs__panel');
        });

        if (!this.sections.length) {
          this.root.classList.remove('is-ready');
          return;
        }

        this.renderTabs();
        this.setupObserver();
        this.setActive(Math.min(this.activeIndex, this.sections.length - 1));
        this.root.classList.add('is-ready');
        if (this.updateHeaderOffset) {
          this.updateHeaderOffset();
          this.recalcStickyMetrics();
        }
      }

      /* Restore the no-JS stacked state: move any absorbed group sections back
         into the document flow (right after the controller host), strip the panel
         markup, and remove the shell. The controller itself stays hidden (never
         gets .is-ready), so on mobile all FAQ groups simply render stacked and
         visible — the plain accordion, identical to the no-JS fallback. */
      teardown() {
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }

        if (this.shell) {
          var panels = Array.from(this.shell.querySelectorAll('.faq-tabs__panel'));
          var anchor = this.host.nextSibling;
          panels.forEach(function (panel) {
            panel.classList.remove('faq-tabs__panel');
            // Re-insert in original order, immediately after the host.
            this.host.parentNode.insertBefore(panel, anchor);
            anchor = panel.nextSibling;
          }, this);

          if (this.shell.parentNode) this.shell.parentNode.removeChild(this.shell);
          this.shell = null;
          this.nav = null;
          this.tablist = null;
          this.panels = null;
          this.root = null;
        }

        this.sections = [];
        this.tabs = [];
        this.intersecting = new Set();
        this.activeIndex = 0;
      }

      renderTabs() {
        var self = this;
        this.tablist.innerHTML = '';
        this.tabs = this.sections.map(function (section, index) {
          var headingEl = section.querySelector('.collapsible-content__heading');
          var label = headingEl ? headingEl.textContent.trim() : 'Category ' + (index + 1);

          if (!section.id) section.id = 'FaqPanel-' + index + '-' + self.seed();
          var tabId = 'FaqNav-' + section.id;

          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'faq-tabs__tab';
          btn.id = tabId;
          btn.setAttribute('aria-controls', section.id);
          btn.textContent = label;

          section.setAttribute('aria-labelledby', tabId);

          btn.addEventListener('click', function () {
            self.scrollToSection(index);
          });

          self.tablist.appendChild(btn);
          return btn;
        });
      }

      seed() {
        this._seed = (this._seed || 0) + 1;
        return this._seed;
      }

      setupObserver() {
        var self = this;
        if (this.observer) this.observer.disconnect();
        this.intersecting = new Set();

        this.observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              var idx = self.sections.indexOf(entry.target);
              if (idx < 0) return;
              if (entry.isIntersecting) self.intersecting.add(idx);
              else self.intersecting.delete(idx);
            });

            if (Date.now() < self.lockUntil) return;
            if (!self.intersecting.size) return;

            var active = Math.min.apply(null, Array.from(self.intersecting));
            self.setActive(active);
          },
          { rootMargin: '-25% 0px -65% 0px', threshold: 0 }
        );

        this.sections.forEach(function (section) {
          self.observer.observe(section);
        });
      }

      scrollToSection(index) {
        if (!this.sections[index]) return;
        this.lockUntil = Date.now() + 700;
        this.setActive(index);
        this.sections[index].scrollIntoView({
          behavior: this.reduceMotion ? 'auto' : 'smooth',
          block: 'start',
        });
      }

      setActive(index) {
        if (!this.tabs.length) return;
        index = Math.max(0, Math.min(index, this.tabs.length - 1));
        if (index === this.activeIndex && this.tabs[index].getAttribute('aria-current') === 'true') {
          return;
        }
        this.activeIndex = index;
        this.tabs.forEach(function (tab, i) {
          if (i === index) tab.setAttribute('aria-current', 'true');
          else tab.removeAttribute('aria-current');
        });
        this.scrollNavIntoView();
      }

      /* Mobile: keep the active tab visible by scrolling the horizontal nav to
         centre it. Picks whichever element actually scrolls horizontally (the nav
         or the tablist, depending on flex sizing); no-op on desktop. */
      scrollNavIntoView() {
        var tab = this.tabs[this.activeIndex];
        if (!tab) return;

        var scroller = null;
        if (this.nav && this.nav.scrollWidth > this.nav.clientWidth + 1) scroller = this.nav;
        else if (this.tablist && this.tablist.scrollWidth > this.tablist.clientWidth + 1) scroller = this.tablist;
        if (!scroller) return;

        var scRect = scroller.getBoundingClientRect();
        var tabRect = tab.getBoundingClientRect();
        var delta = tabRect.left - scRect.left - (scRect.width - tabRect.width) / 2;
        scroller.scrollTo({
          left: scroller.scrollLeft + delta,
          behavior: this.reduceMotion ? 'auto' : 'smooth',
        });
      }

      /* Scroll: only keep --faq-nav-offset fresh (used by panel scroll-margin so
         tab clicks land below a visible header). Change-guarded + rAF, so most
         frames do nothing — the nav centring itself is native sticky, no JS. */
      onScroll() {
        if (this.scrollRaf) return;
        var self = this;
        this.scrollRaf = requestAnimationFrame(function () {
          self.scrollRaf = null;
          self.updateHeaderOffset();
        });
      }

      onResize() {
        if (this.resizeRaf) return;
        var self = this;
        this.resizeRaf = requestAnimationFrame(function () {
          self.resizeRaf = null;
          self.updateHeaderOffset();
          self.recalcStickyMetrics();
        });
      }

      updateHeaderOffset() {
        if (!this.root) return;
        var offset = this.header ? Math.max(0, this.header.getBoundingClientRect().bottom) : 0;
        if (offset === this.headerOffset) return; // skip redundant style writes
        this.headerOffset = offset;
        this.root.style.setProperty('--faq-nav-offset', offset + 'px');
      }

      /* Compute the two values that let NATIVE position:sticky centre the desktop
         tab list — no per-frame JS, so scrolling is butter-smooth:

         1. --faq-list-top : the sticky rest offset. Set so the list's centre sits
            at the viewport centre once pinned (the header scrolls away here, so the
            whole viewport is available during deep scroll).
         2. nav padding-top : pushes the list's natural (unpinned) position down to
            the centre of the section's *visible* part at the top of the page, so it
            doesn't sit at the section's top edge before sticky engages. As the user
            scrolls, the list drifts up naturally until it reaches the sticky offset,
            then pins — all handled by the browser.

         Recomputed on resize / rebuild only (layout-affecting work kept off the
         scroll path). No-op on mobile (horizontal sticky bar). */
      recalcStickyMetrics() {
        if (!this.nav || !this.tablist || !this.root) return;

        if (!(window.matchMedia && window.matchMedia('(min-width: 990px)').matches)) {
          this.nav.style.paddingTop = '';
          this.root.style.removeProperty('--faq-list-top');
          return;
        }

        var listH = this.tablist.offsetHeight;
        var ih = window.innerHeight;

        var stickyTop = Math.round(ih / 2 - listH / 2);
        if (stickyTop < 0) stickyTop = 0;

        // Document-Y of the section top (stable regardless of scroll position;
        // a nav's own padding doesn't move its border-box top).
        var navTopDoc = this.nav.getBoundingClientRect().top + window.scrollY;
        var pad = Math.round((ih - navTopDoc) / 2 - listH / 2);
        if (pad < 0) pad = 0;

        this.root.style.setProperty('--faq-list-top', stickyTop + 'px');
        this.nav.style.paddingTop = pad + 'px';
      }

      /* ---- Theme editor sync ---- */
      onSectionLoad(event) {
        if (this.shell && this.shell.contains(event.target)) return;
        this.build();
      }

      onSectionUnload(event) {
        if (this.shell && this.shell.contains(event.target)) {
          event.target.classList.remove('faq-tabs__panel');
          this.build();
        }
      }

      onSectionSelect(event) {
        var panel = event.target.closest ? event.target.closest('.faq-tabs__panel') : null;
        if (panel) {
          var idx = this.sections.indexOf(panel);
          if (idx > -1) this.scrollToSection(idx);
        }
      }
    }
  );
}
